import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  MerchantService,
  PaymentFacilitatorType,
  PaymentService,
  PayoutInterval,
  PayoutSummary,
} from '@galaxy/billing';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatTooltip } from '@angular/material/tooltip';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { PayoutListComponent } from '../payout-list/payout-list.component';

import { RetailPaymentsOnboardingComponent } from '../../retail-payments/onboarding/retail-payments-onboarding.component';

@Component({
  selector: 'smb-invoicing-payouts',
  templateUrl: './payouts.component.html',
  styleUrls: ['./payouts.component.scss'],
  standalone: true,
  imports: [
    GalaxyPageModule,
    AsyncPipe,
    MatIcon,
    TranslateModule,
    MatCard,
    MatCardTitle,
    MatCardHeader,
    MatTooltip,
    MatCardContent,
    GalaxyPipesModule,
    PayoutListComponent,
    NgIf,
    NgForOf,
    RetailPaymentsOnboardingComponent,
  ],
})
export class PayoutsComponent implements OnInit {
  DateFormat = DateFormat;

  retailBalance$: Observable<PayoutSummary>;
  supportedRegion$: Observable<boolean>;
  canAcceptPayment$: Observable<boolean>;
  loading$: Observable<boolean>;
  payoutSchedule$: Observable<string>;
  redirectToStripe$: Observable<boolean>;
  @Input() merchantId$: Observable<string>;
  @Output() payoutSelected: EventEmitter<string> = new EventEmitter();
  @Output() onboardingUnsupportedRegionAction: EventEmitter<boolean> = new EventEmitter();
  @Output() onboardingCallToAction: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private merchantService: MerchantService,
    private paymentService: PaymentService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    const retailStatus$ = this.merchantId$.pipe(
      switchMap((id) => this.paymentService.retailStatus(id)),
      catchError(() => {
        return of({ supportedRegion: false, canAcceptPayment: false });
      }),
      shareReplay(1),
    );
    this.supportedRegion$ = retailStatus$.pipe(map((rs) => rs.supportedRegion));
    this.canAcceptPayment$ = retailStatus$.pipe(map((rs) => rs.canAcceptPayment));
    this.loading$ = retailStatus$.pipe(
      switchMap((_) => of(false)),
      startWith(true),
    );

    this.redirectToStripe$ = this.merchantId$.pipe(
      switchMap((id) => this.paymentService.getRetailProvider(id)),
      map((provider) => provider.paymentFacilitatorType === PaymentFacilitatorType.STANDARD_STRIPE),
      catchError(() => of(false)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.retailBalance$ = combineLatest([this.merchantId$, this.redirectToStripe$]).pipe(
      switchMap(([partnerId, redirectToStripe]) => {
        if (redirectToStripe) {
          return of({} as PayoutSummary);
        } else {
          return this.merchantService.getPayoutSummary(partnerId);
        }
      }),
    );

    this.payoutSchedule$ = this.retailBalance$.pipe(
      map((payoutSummary) => {
        const key = getPayoutScheduleTranslationKey(payoutSummary.payoutInterval);
        return this.translateService.instant(key, {
          delay: payoutSummary.payoutDelayDays,
        });
      }),
    );
  }

  onPayoutSelected(payoutId: string) {
    this.payoutSelected.emit(payoutId);
  }

  onboardingUnsupportedRegionClicked(event: boolean): void {
    if (!event) {
      return;
    }
    this.onboardingUnsupportedRegionAction.emit(event);
  }

  onboardingCallToActionClicked(event: boolean): void {
    if (!event) {
      return;
    }
    this.onboardingCallToAction.emit(event);
  }
}

/**
 * Returns the correct translation key for a payout schedule with a given interval
 * @param payoutInterval - 'daily', 'weekly', or 'monthly'
 */
export function getPayoutScheduleTranslationKey(payoutInterval: PayoutInterval): string {
  switch (payoutInterval) {
    case PayoutInterval.Daily:
      return 'PAYOUTS_PAGE.CURRENT_BALANCE.PAYOUT_SCHEDULE_DAILY';
    case PayoutInterval.Weekly:
      return 'PAYOUTS_PAGE.CURRENT_BALANCE.PAYOUT_SCHEDULE_WEEKLY';
    case PayoutInterval.Monthly:
      return 'PAYOUTS_PAGE.CURRENT_BALANCE.PAYOUT_SCHEDULE_MONTHLY';
  }
  return 'PAYOUTS_PAGE.CURRENT_BALANCE.PAYOUT_SCHEDULE_MANUAL';
}
